'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { toast, Toaster } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface Visitatore {
  id: string;
  nome_visitatore: string;
  cognome_visitatore: string;
  azienda: string;
  documento_identita: string;
  device_id: string | null;
  reparto_id: string | null;
  data_ingresso: string;
  data_uscita: string | null;
  durata_minuti: number | null;
  motivo_visita: string;
  lavori_eseguiti: string;
  badge_numero: string;
  note: string;
  device?: { hostname: string } | null;
  reparto?: { nome: string } | null;
}

interface Device { id: string; hostname: string; }
interface Reparto { id: string; nome: string; }

const emptyVisitatore = {
  nome_visitatore: '', cognome_visitatore: '', azienda: '', documento_identita: '',
  device_id: null as string | null, reparto_id: null as string | null,
  data_ingresso: new Date().toISOString().slice(0, 16),
  data_uscita: '' as string,
  durata_minuti: null as number | null,
  motivo_visita: '', lavori_eseguiti: '', badge_numero: '', note: '',
};

export default function VisitatorContent() {
  const [visitatori, setVisitatori] = useState<Visitatore[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [reparti, setReparti] = useState<Reparto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState<'all' | 'in_corso' | 'usciti'>('all');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<Visitatore | null>(null);
  const [formData, setFormData] = useState<typeof emptyVisitatore>({ ...emptyVisitatore });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string }>({ open: false, id: '', name: '' });
  const { canWrite } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, dRes, rRes] = await Promise.all([
        supabase.from('visitatori_tecnici').select('*, device:devices(hostname), reparto:reparti(nome)').order('data_ingresso', { ascending: false }),
        supabase.from('devices').select('id, hostname').order('hostname'),
        supabase.from('reparti').select('id, nome').order('nome'),
      ]);
      if (vRes.error) throw vRes.error;
      setVisitatori(vRes.data || []);
      setDevices(dRes.data || []);
      setReparti(rRes.data || []);
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = visitatori.filter(v => {
    const name = `${v.nome_visitatore} ${v.cognome_visitatore} ${v.azienda}`.toLowerCase();
    if (search && !name.includes(search.toLowerCase())) return false;
    if (filterOpen === 'in_corso' && v.data_uscita) return false;
    if (filterOpen === 'usciti' && !v.data_uscita) return false;
    return true;
  });

  const openCreate = () => {
    setEditItem(null);
    setFormData({ ...emptyVisitatore, data_ingresso: new Date().toISOString().slice(0, 16) });
    setShowForm(true);
  };

  const openEdit = (v: Visitatore) => {
    setEditItem(v);
    setFormData({
      nome_visitatore: v.nome_visitatore, cognome_visitatore: v.cognome_visitatore,
      azienda: v.azienda, documento_identita: v.documento_identita,
      device_id: v.device_id, reparto_id: v.reparto_id,
      data_ingresso: v.data_ingresso.slice(0, 16),
      data_uscita: v.data_uscita ? v.data_uscita.slice(0, 16) : '',
      durata_minuti: v.durata_minuti,
      motivo_visita: v.motivo_visita, lavori_eseguiti: v.lavori_eseguiti,
      badge_numero: v.badge_numero, note: v.note,
    });
    setShowForm(true);
  };

  const handleRegistraUscita = async (v: Visitatore) => {
    const now = new Date().toISOString();
    const ingresso = new Date(v.data_ingresso);
    const uscita = new Date(now);
    const durata = Math.round((uscita.getTime() - ingresso.getTime()) / 60000);
    try {
      const { error } = await supabase.from('visitatori_tecnici').update({ data_uscita: now, durata_minuti: durata, updated_at: now }).eq('id', v.id);
      if (error) throw error;
      toast.success('Uscita registrata');
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const handleSave = async () => {
    if (!formData.nome_visitatore || !formData.cognome_visitatore) { toast.error('Nome e cognome obbligatori'); return; }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        data_uscita: formData.data_uscita || null,
        device_id: formData.device_id || null,
        reparto_id: formData.reparto_id || null,
      };
      if (editItem) {
        const { error } = await supabase.from('visitatori_tecnici').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', editItem.id);
        if (error) throw error;
        toast.success('Visita aggiornata');
      } else {
        const { error } = await supabase.from('visitatori_tecnici').insert(payload);
        if (error) throw error;
        toast.success('Visita registrata');
      }
      setShowForm(false);
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase.from('visitatori_tecnici').delete().eq('id', deleteConfirm.id);
      if (error) throw error;
      toast.success('Visita eliminata');
      setDeleteConfirm({ open: false, id: '', name: '' });
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const inCorso = visitatori.filter(v => !v.data_uscita).length;

  return (
    <div className="flex flex-col h-full bg-background">
      <Toaster richColors position="top-right" />
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-surface">
        <div>
          <h1 className="text-2xl font-bold text-white">Registro Visitatori Tecnici</h1>
          <p className="text-sm text-muted-color mt-0.5">Chi è entrato, quando, su quale dispositivo ha operato</p>
        </div>
        <div className="flex items-center gap-3">
          {inCorso > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/20 border border-amber-500/30 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-sm font-semibold text-amber-400">{inCorso} in corso</span>
            </div>
          )}
          {canWrite() && (
            <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg font-semibold transition-colors">
              <Icon name="PlusIcon" size={18} /> Registra Visita
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 px-6 py-3 border-b border-border">
        <div className="relative flex-1 max-w-sm">
          <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-color" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca visitatore o azienda..."
            className="w-full bg-surface-elevated border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-muted-color focus:outline-none focus:border-sky-500" />
        </div>
        <div className="flex gap-1">
          {([['all', 'Tutti'], ['in_corso', 'In Corso'], ['usciti', 'Usciti']] as const).map(([k, v]) => (
            <button key={k} onClick={() => setFilterOpen(k)}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${filterOpen === k ? 'bg-sky-500/20 text-sky-400' : 'text-muted-color hover:text-white hover:bg-surface-elevated'}`}>
              {v}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-muted-color">
            <Icon name="UserGroupIcon" size={48} className="mb-3 opacity-30" />
            <p className="text-lg font-semibold">Nessuna visita trovata</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(v => (
              <div key={v.id} className="bg-surface border border-border rounded-xl p-5 hover:border-sky-500/40 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${v.data_uscita ? 'bg-surface-elevated' : 'bg-amber-500/20'}`}>
                      <Icon name="UserIcon" size={20} className={v.data_uscita ? 'text-muted-color' : 'text-amber-400'} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-white">{v.nome_visitatore} {v.cognome_visitatore}</h3>
                        {!v.data_uscita && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-semibold">In corso</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-color">{v.azienda}</p>
                      <div className="flex flex-wrap gap-3 mt-2 text-sm text-secondary-color">
                        <span className="flex items-center gap-1">
                          <Icon name="ClockIcon" size={12} className="text-muted-color" />
                          {new Date(v.data_ingresso).toLocaleString('it-IT')}
                        </span>
                        {v.data_uscita && (
                          <span className="flex items-center gap-1">
                            <Icon name="ArrowRightIcon" size={12} className="text-muted-color" />
                            {new Date(v.data_uscita).toLocaleString('it-IT')}
                            {v.durata_minuti && <span className="text-muted-color">({v.durata_minuti}min)</span>}
                          </span>
                        )}
                        {v.device?.hostname && (
                          <span className="flex items-center gap-1">
                            <Icon name="ServerIcon" size={12} className="text-muted-color" />
                            {v.device.hostname}
                          </span>
                        )}
                        {v.reparto?.nome && (
                          <span className="flex items-center gap-1">
                            <Icon name="BuildingOfficeIcon" size={12} className="text-muted-color" />
                            {v.reparto.nome}
                          </span>
                        )}
                      </div>
                      {v.motivo_visita && <p className="text-sm text-secondary-color mt-1"><span className="text-muted-color">Motivo:</span> {v.motivo_visita}</p>}
                      {v.lavori_eseguiti && <p className="text-sm text-secondary-color mt-0.5"><span className="text-muted-color">Lavori:</span> {v.lavori_eseguiti}</p>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!v.data_uscita && canWrite() && (
                      <button onClick={() => handleRegistraUscita(v)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 rounded-lg text-sm font-semibold transition-colors">
                        <Icon name="ArrowRightOnRectangleIcon" size={14} /> Registra Uscita
                      </button>
                    )}
                    {canWrite() && (
                      <>
                        <button onClick={() => openEdit(v)} className="p-2 rounded-lg hover:bg-sky-500/10 text-sky-400 transition-colors"><Icon name="PencilIcon" size={14} /></button>
                        <button onClick={() => setDeleteConfirm({ open: true, id: v.id, name: `${v.nome_visitatore} ${v.cognome_visitatore}` })}
                          className="p-2 rounded-lg hover:bg-red-500/10 text-red-400 transition-colors"><Icon name="TrashIcon" size={14} /></button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-xl font-bold text-white">{editItem ? 'Modifica' : 'Registra'} Visita</h2>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-lg hover:bg-surface-elevated text-muted-color"><Icon name="XMarkIcon" size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Nome *</label>
                  <input value={formData.nome_visitatore} onChange={e => setFormData({ ...formData, nome_visitatore: e.target.value })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                    placeholder="Mario" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Cognome *</label>
                  <input value={formData.cognome_visitatore} onChange={e => setFormData({ ...formData, cognome_visitatore: e.target.value })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                    placeholder="Rossi" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Azienda</label>
                  <input value={formData.azienda} onChange={e => setFormData({ ...formData, azienda: e.target.value })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                    placeholder="Nome azienda" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Badge N°</label>
                  <input value={formData.badge_numero} onChange={e => setFormData({ ...formData, badge_numero: e.target.value })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                    placeholder="BADGE-001" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Documento Identità</label>
                <input value={formData.documento_identita} onChange={e => setFormData({ ...formData, documento_identita: e.target.value })}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                  placeholder="CI AB1234567" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Dispositivo</label>
                  <select value={formData.device_id || ''} onChange={e => setFormData({ ...formData, device_id: e.target.value || null })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500">
                    <option value="">Nessuno</option>
                    {devices.map(d => <option key={d.id} value={d.id}>{d.hostname}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Reparto</label>
                  <select value={formData.reparto_id || ''} onChange={e => setFormData({ ...formData, reparto_id: e.target.value || null })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500">
                    <option value="">Nessuno</option>
                    {reparti.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Data/Ora Ingresso *</label>
                  <input type="datetime-local" value={formData.data_ingresso} onChange={e => setFormData({ ...formData, data_ingresso: e.target.value })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Data/Ora Uscita</label>
                  <input type="datetime-local" value={formData.data_uscita} onChange={e => setFormData({ ...formData, data_uscita: e.target.value })}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Motivo Visita</label>
                <input value={formData.motivo_visita} onChange={e => setFormData({ ...formData, motivo_visita: e.target.value })}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                  placeholder="es. Manutenzione preventiva" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Lavori Eseguiti</label>
                <textarea value={formData.lavori_eseguiti} onChange={e => setFormData({ ...formData, lavori_eseguiti: e.target.value })} rows={2}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500 resize-none"
                  placeholder="Descrizione lavori eseguiti..." />
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-border">
              <button onClick={() => setShowForm(false)} className="flex-1 px-4 py-2.5 border border-border rounded-lg text-secondary-color hover:bg-surface-elevated font-semibold transition-colors">Annulla</button>
              <button onClick={handleSave} disabled={saving || !formData.nome_visitatore || !formData.cognome_visitatore}
                className="flex-1 px-4 py-2.5 bg-sky-500 hover:bg-sky-600 disabled:opacity-50 text-white rounded-lg font-semibold transition-colors">
                {saving ? 'Salvataggio...' : editItem ? 'Aggiorna' : 'Registra'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Elimina Visita"
        message={`Sei sicuro di voler eliminare la visita di "${deleteConfirm.name}"?`}
        confirmLabel="Elimina"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ open: false, id: '', name: '' })}
      />
    </div>
  );
}
